import { useRoundDownloadRingPercent } from "@client/iphone/lib/roundDownloadProgress";
import { isIPadCapacitor } from "@client/lib/isIPad";
import {
  lazyWithBackgroundWarm,
  warmChunkNow,
} from "@client/utils/backgroundChunkWarming";
import { cn } from "@client/utils/cn";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Skeleton,
} from "@sfc/ui";
import { ListVideoIcon } from "lucide-react";
import { Suspense, useState } from "react";
import { useLocation } from "wouter";

import { QueueDownloadRing } from "./QueueDownloadRing";
import { useQueueIconPulse } from "./queueIconPulse";
import { roundIdFromPathname } from "./roundIdFromPathname";

const loadQueueDrawerBody = async () => ({
  default: (await import("./QueueDrawerBody")).QueueDrawerBody,
});

const LazyQueueDrawerBody = lazyWithBackgroundWarm(loadQueueDrawerBody);
let queueDrawerBodyPreloaded = false;

function preloadQueueDrawerBody(): void {
  if (queueDrawerBodyPreloaded) return;
  queueDrawerBodyPreloaded = true;
  warmChunkNow(LazyQueueDrawerBody.preload);
}

export function QueueDrawer() {
  const [open, setOpen] = useState(false);
  // Pop the header glyph whenever a Save / Up Next toggle fires on a card, so
  // the top-bar Queue icon echoes the card's pop animation.
  const pulseKey = useQueueIconPulse();
  // Derive the round id from the URL — the same stable source QueueProvider
  // keys/hydrates the store on. Reading it from ImpersonateContext instead would
  // lag a round switch (the context updates only when Round remounts), so the
  // drawer could hydrate/query/deep-link the previous round against the new
  // round's store.
  const [location] = useLocation();
  const roundId = roundIdFromPathname(location);
  // iPad only: a determinate progress ring around the header Queue icon while
  // this round's downloads are in flight, so the funder sees how far along the
  // batch is with the drawer closed. `-1` = nothing downloading (hide the ring);
  // 0..100 mirrors the drawer's "Download all" bar exactly (one shared source).
  // On the web build no downloads run, so the percent stays -1 — the
  // isIPadCapacitor gate just makes the "iPad only" intent explicit.
  const downloadPercent = useRoundDownloadRingPercent(roundId ?? "");
  const showDownloadRing = isIPadCapacitor() && downloadPercent >= 0;

  if (roundId == null) {
    return null;
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger
        type="button"
        title={
          showDownloadRing
            ? `Queue — downloading videos (${downloadPercent}%)`
            : "Queue"
        }
        aria-label={
          showDownloadRing
            ? `Open queue, downloading videos, ${downloadPercent}% complete`
            : "Open queue"
        }
        onPointerEnter={preloadQueueDrawerBody}
        onPointerDown={preloadQueueDrawerBody}
        onFocus={preloadQueueDrawerBody}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-text-secondary transition-colors hover:bg-surface-hover hover:text-text-primary"
      >
        {/* Ping ring — remounts on each pulse (fresh key) so the finite CSS
            animation replays; only mounted after a real tap so it never shows on
            load. Behind the glyph and non-interactive. */}
        {pulseKey > 0 && (
          <span
            key={`ping-${pulseKey}`}
            aria-hidden
            className="queue-ping pointer-events-none absolute inset-0 rounded-full border-2 border-primary"
          />
        )}
        {/* Download progress ring — fills around the glyph as this round's
            download batch completes (iPad). Non-interactive, behind the glyph. */}
        {showDownloadRing && <QueueDownloadRing percent={downloadPercent} />}
        <ListVideoIcon
          key={pulseKey}
          className={cn("size-5", pulseKey > 0 && "save-pop")}
        />
      </SheetTrigger>

      <SheetContent side="right" className="w-95 sm:max-w-105">
        <SheetHeader>
          <SheetTitle>Queue</SheetTitle>
        </SheetHeader>

        {open && (
          <Suspense fallback={<QueueDrawerBodyPending />}>
            <LazyQueueDrawerBody
              roundId={roundId}
              onNavigate={() => setOpen(false)}
            />
          </Suspense>
        )}
      </SheetContent>
    </Sheet>
  );
}

function QueueDrawerBodyPending() {
  return (
    <div
      aria-label="Loading queue"
      className="flex flex-1 min-h-0 flex-col gap-3 px-6"
    >
      <div className="grid h-9 grid-cols-3 gap-1 rounded-md bg-muted p-1">
        <Skeleton className="h-full rounded-sm bg-surface-base" />
        <Skeleton className="h-full rounded-sm" />
        <Skeleton className="h-full rounded-sm" />
      </div>
      <div className="flex flex-col gap-3">
        {[0, 1, 2].map((index) => (
          <Skeleton key={index} className="h-20 w-full rounded-lg" />
        ))}
      </div>
    </div>
  );
}
